import { useEffect, useMemo, useRef, useState } from 'react';
import { hostSummary, hostBookings, hostPayouts } from '../api/host';
import { listHostInvoices } from '../api/invoices';
import { formatDateOnly } from '../utils/dateFormat';
import { useCurrency } from '../components/CurrencyContext';

const EMPTY_SUMMARY = { properties: 0, bookings: [], payouts: [] };

const statusTone = (status) => {
  const value = (status || '').toLowerCase();
  if (['confirmed', 'completed', 'paid', 'approved', 'success'].some((k) => value.includes(k))) {
    return 'text-emerald-700 bg-emerald-50 border-emerald-100';
  }
  if (['pending', 'processing', 'hold'].some((k) => value.includes(k))) {
    return 'text-amber-700 bg-amber-50 border-amber-100';
  }
  if (['cancel', 'reject', 'fail'].some((k) => value.includes(k))) {
    return 'text-rose-700 bg-rose-50 border-rose-100';
  }
  return 'text-slate-700 bg-slate-50 border-slate-200';
};

const formatStatusLabel = (value) => String(value || 'unknown').replace(/_/g, ' ');

const StatCard = ({ label, value, tone }) => {
  const ring = {
    emerald: 'text-emerald-700 bg-emerald-50 ring-emerald-100',
    blue: 'text-blue-700 bg-blue-50 ring-blue-100',
    amber: 'text-amber-700 bg-amber-50 ring-amber-100',
  }[tone || 'emerald'];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between pb-2">
        <p className="text-sm font-medium text-slate-600">{label}</p>
        <span className={`h-8 w-8 rounded-full ring-4 ${ring} flex items-center justify-center`}>
          <span className="h-3 w-3 rounded-full bg-current" />
        </span>
      </div>
      <p className="text-3xl font-semibold text-slate-900">{value}</p>
    </div>
  );
};

const ListCard = ({ title, hint, items, loading, renderItem, emptyText }) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
      <div>
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
      <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700">Last 5</span>
    </div>
    {loading && <div className="p-4 text-sm text-slate-500">Loading...</div>}
    {!loading && items.length === 0 && <div className="p-4 text-sm text-slate-500">{emptyText}</div>}
    {!loading && items.length > 0 && <div className="divide-y divide-slate-100">{items.map(renderItem)}</div>}
  </div>
);

const HostDashboard = ({ accessToken }) => {
  const { formatCurrency } = useCurrency();
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [bookings, setBookings] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const lastTokenRef = useRef(null);

  useEffect(() => {
    const token = typeof accessToken === 'string' ? accessToken : '';
    if (lastTokenRef.current === token) return;
    lastTokenRef.current = token;

    if (!token) {
      setSummary((prev) =>
        prev?.properties === 0 && (prev?.bookings?.length || 0) === 0 && (prev?.payouts?.length || 0) === 0
          ? prev
          : EMPTY_SUMMARY
      );
      setBookings((prev) => (prev.length === 0 ? prev : []));
      setPayouts((prev) => (prev.length === 0 ? prev : []));
      setError((prev) => (prev ? prev : 'Please log in to view your dashboard.'));
      setLoading(false);
      return;
    }
    let active = true;
    const load = async () => {
      setLoading(true);
      setError((prev) => (prev ? '' : prev));
      try {
        const [sum, b, p, inv] = await Promise.all([
          hostSummary(token),
          hostBookings(token),
          hostPayouts(token).catch(() => []),
          listHostInvoices({ limit: 5 }, token).catch(() => ({ data: [] })),
        ]);
        if (!active) return;
        setSummary(sum);
        setBookings(Array.isArray(b) ? b : b?.items || []);
        setPayouts(Array.isArray(p) ? p : p?.items || []);
        setInvoices(inv?.data || []);
      } catch (err) {
        if (!active) return;
        setError(err.message || 'Failed to load dashboard');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [accessToken]);

  const payoutCounts = useMemo(
    () =>
      payouts.reduce((acc, item) => {
        const k = item.status || 'unknown';
        acc[k] = (acc[k] || 0) + 1;
        return acc;
      }, {}),
    [payouts]
  );

  const kpis = [
    { label: 'Total properties', value: summary.properties || 0, tone: 'emerald' },
    { label: 'Bookings', value: bookings.length, tone: 'blue' },
    { label: 'Pending payouts', value: payoutCounts.PENDING || 0, tone: 'amber' },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Homevilas</p>
        <h1 className="text-3xl font-bold text-slate-900">Host Dashboard</h1>
        <p className="text-sm text-slate-600">Quick snapshot of your listings, bookings, and payouts.</p>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {kpis.map((kpi) => (
          <StatCard key={kpi.label} label={kpi.label} value={kpi.value} tone={kpi.tone} />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ListCard
          title="Recent bookings"
          hint="Latest bookings across your properties"
          items={bookings.slice(0, 5)}
          loading={loading}
          emptyText="No bookings yet."
          renderItem={(b) => (
            <div key={b.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/60">
              <div>
                <p className="text-sm font-semibold text-slate-900">{b.booking_code || `#${b.id}`}</p>
                  <p className="text-xs text-slate-500">
                    {formatDateOnly(b.check_in_date, 'TBD')} &bull; {formatStatusLabel(b.payment_status || 'unpaid')}
                  </p>
                </div>
              <span className={`text-[11px] font-semibold px-3 py-1 rounded-full border ${statusTone(b.booking_status)}`}>
                {formatStatusLabel(b.booking_status || 'pending')}
              </span>
            </div>
          )}
        />

        <ListCard
          title="Payouts"
          hint="Next and recent payouts"
          items={payouts.slice(0, 5)}
          loading={loading}
          emptyText="No payouts yet."
          renderItem={(p) => (
            <div key={p.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/60">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {formatCurrency(p.amount, p.currency || 'INR')}
                </p>
                <p className="text-xs text-slate-500">
                  {p.period_start || 'N/A'} - {p.period_end || 'N/A'}
                </p>
              </div>
              <span className={`text-[11px] font-semibold px-3 py-1 rounded-full border ${statusTone(p.status)}`}>
                {p.status || 'pending'}
              </span>
            </div>
          )}
        />
      </div>

      <ListCard
        title="Invoices"
        hint="Settlement statements and commission invoices"
        items={invoices.slice(0, 5)}
        loading={loading}
        emptyText="No invoices yet."
        renderItem={(inv) => {
          const total = inv.totals?.grandTotal ?? inv.totals?.total ?? 0;
          return (
            <div key={inv.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/60">
              <div>
                <p className="text-sm font-semibold text-slate-900">{inv.invoiceNumber}</p>
                <p className="text-xs text-slate-500">
                  {inv.invoiceType?.replace(/_/g, ' ')} &bull; {inv.stayDetails?.bookingCode || inv.bookingId}
                </p>
              </div>
              <span className="text-xs font-semibold text-slate-700">
                {formatCurrency(total, inv.currency || 'INR')}
              </span>
            </div>
          );
        }}
      />
    </div>
  );
};

export default HostDashboard;
